import { isAuthError, useFavoritePage, useDeleteFavorites } from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductCardSkeleton } from "../components/TgSkeleton";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { useAuth } from "../lib/auth";

/** Pastel gradient placeholder backgrounds — cycle by index, same as catalog */
const TILE_BG = [
  "linear-gradient(135deg,#FFE5DD,#FFD3C2)",
  "linear-gradient(135deg,#E0F7F1,#B8E8DD)",
  "linear-gradient(135deg,#EFEBFF,#D7CCFF)",
  "linear-gradient(135deg,#FFF8DA,#FFE5A0)",
  "linear-gradient(135deg,#FFE0E9,#FFC8D6)",
  "linear-gradient(135deg,#FFF0EB,#FFD3C2)",
] as const;

type TabType = "spu" | "shop";

export function FavoritePage() {
  const { t } = useTranslation("favorite");
  const auth = useAuth();
  const authToken = auth.token;

  const [tab, setTab] = useState<TabType>("spu");
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const spuQuery = useFavoritePage(authToken, {
    type: "spu",
    pageNo: 1,
    pageSize: 30,
  });
  const shopQuery = useFavoritePage(authToken, {
    type: "shop",
    pageNo: 1,
    pageSize: 30,
  });

  const activeQuery = tab === "spu" ? spuQuery : shopQuery;
  const records = activeQuery.data?.records ?? [];
  const spuTotal = spuQuery.data?.total ?? 0;
  const shopTotal = shopQuery.data?.total ?? 0;
  const totalAll = spuTotal + shopTotal;

  const deleteMutation = useDeleteFavorites(authToken);

  const sessionExpired = activeQuery.isError && isAuthError(activeQuery.error);

  // Stable item key used for selection bookkeeping
  function itemKey(spuNo: string, channel: string) {
    return `${channel}::${spuNo}`;
  }

  function toggleSelect(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(records.map((r) => itemKey(r.spuNo, r.channel))));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  function exitEditMode() {
    setEditMode(false);
    clearSelection();
  }

  async function handleDelete() {
    if (selected.size === 0) return;
    // Collect spuNos for selected items that belong to current tab
    const spuNos = records
      .filter((r) => selected.has(itemKey(r.spuNo, r.channel)))
      .map((r) => r.spuNo);
    if (spuNos.length === 0) return;
    try {
      await deleteMutation.mutateAsync({ type: tab, spuNos });
      exitEditMode();
    } catch {
      // mutation failure: stay in edit mode, user can retry
    }
  }

  // Header right slot: "编辑" / "完成" button
  const headerRight = auth.status === "signed-in" && records.length > 0 ? (
    <button
      type="button"
      onClick={() => {
        if (editMode) {
          exitEditMode();
        } else {
          setEditMode(true);
        }
      }}
      style={{
        background: "none",
        border: "none",
        fontSize: 12,
        fontWeight: 700,
        color: "var(--color-text)",
        cursor: "pointer",
        padding: "4px 0",
      }}
    >
      {editMode ? t("edit.done") : t("edit.start")}
    </button>
  ) : undefined;

  // Header title with total badge (design: "收藏 42")
  const headerTitle =
    totalAll > 0
      ? `${t("head.title")} ${totalAll}`
      : t("head.title");

  return (
    <TgPage tabbar={false}>
      <TgHeader title={headerTitle} back right={headerRight} />

      {/* ── Tab bar ── */}
      <div
        style={{
          display: "flex",
          gap: 4,
          padding: "8px 12px",
          background: "#fff",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <button
          type="button"
          onClick={() => { setTab("spu"); exitEditMode(); }}
          style={{
            background: tab === "spu" ? "var(--color-text)" : "transparent",
            color: tab === "spu" ? "#fff" : "var(--color-text-muted)",
            padding: "5px 10px",
            borderRadius: 8,
            fontSize: 10,
            fontWeight: 700,
            border: 0,
            cursor: "pointer",
          }}
        >
          {t("tabs.spu")} {spuTotal > 0 ? spuTotal : ""}
        </button>
        <button
          type="button"
          onClick={() => { setTab("shop"); exitEditMode(); }}
          style={{
            background: tab === "shop" ? "var(--color-text)" : "transparent",
            color: tab === "shop" ? "#fff" : "var(--color-text-muted)",
            padding: "5px 10px",
            borderRadius: 8,
            fontSize: 10,
            fontWeight: 700,
            border: 0,
            cursor: "pointer",
          }}
        >
          {t("tabs.shop")} {shopTotal > 0 ? shopTotal : ""}
        </button>
      </div>

      {/* ── Price-drop banner (spu tab only, shown when tab=spu and we have records) ── */}
      {tab === "spu" && !activeQuery.isLoading && records.length > 0 && (
        <div
          style={{
            background: "var(--color-sticker-yellow-soft, #FFFBE6)",
            borderBottom: "1px solid var(--color-sticker-yellow, #FFE566)",
            padding: "10px 12px",
            fontSize: 11,
            color: "var(--color-sticker-yellow-fg, #7A5C00)",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <span>📉</span>
          <span style={{ flex: 1 }}>
            <strong>{t("tip.price_t", { n: 0 })}</strong>
            {t("tip.price_s", { amount: formatPrice(0, "ru-RU", "RUB") })}
          </span>
        </div>
      )}

      <div className="tg-body" style={{ flex: 1 }}>

        {/* ── Loading skeletons ── */}
        {activeQuery.isLoading && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              padding: "12px",
            }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* ── Auth error / session expired ── */}
        {activeQuery.isError && (
          <div
            style={{
              margin: "16px 12px",
              padding: "16px",
              textAlign: "center",
              background: "var(--color-danger-soft, #FFF0F0)",
              borderRadius: 12,
            }}
          >
            <strong style={{ fontSize: 13 }}>
              {sessionExpired ? t("state.expired_t") : t("state.error_t")}
            </strong>
            <div style={{ marginTop: 10 }}>
              {sessionExpired ? (
                <Link
                  to={"/profile" as never}
                  style={{
                    display: "inline-block",
                    padding: "7px 16px",
                    background: "var(--color-cta)",
                    color: "#fff",
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 12,
                    textDecoration: "none",
                  }}
                >
                  {t("state.signin_cta")}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => activeQuery.refetch()}
                  style={{
                    padding: "7px 16px",
                    border: "1px solid var(--color-border)",
                    borderRadius: 999,
                    background: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {t("state.error_retry")}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Empty state ── */}
        {activeQuery.isSuccess && records.length === 0 && (
          <div style={{ padding: "16px 12px" }}>
            <TgEmpty
              icon={tab === "spu" ? "❤" : "🏪"}
              title={tab === "spu" ? t("empty.spu_t") : t("empty.shop_t")}
              hint={tab === "spu" ? t("empty.spu_s") : t("empty.shop_s")}
              cta={
                <Link
                  to={tab === "spu" ? ("/catalog" as never) : ("/catalog" as never)}
                  style={{
                    display: "inline-block",
                    padding: "8px 18px",
                    background: "var(--color-cta)",
                    color: "#fff",
                    borderRadius: 999,
                    fontWeight: 800,
                    fontSize: 12,
                    textDecoration: "none",
                  }}
                >
                  {tab === "spu" ? t("empty.spu_cta") : t("empty.shop_cta")}
                </Link>
              }
            />
          </div>
        )}

        {/* ── Product grid (spu tab) ── */}
        {activeQuery.isSuccess && records.length > 0 && tab === "spu" && (
          <>
            {/* Edit mode: select-all + delete bar */}
            {editMode && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  background: "#fff",
                  borderBottom: "1px solid var(--color-border)",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  onClick={selected.size === records.length ? clearSelection : selectAll}
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--color-text-muted)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {selected.size === records.length ? "☑ " : "☐ "}
                  {t("edit.selected", { n: selected.size })}
                </button>
                {selected.size > 0 && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    style={{
                      padding: "6px 14px",
                      background: "var(--color-danger, #FF3B30)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: deleteMutation.isPending ? "not-allowed" : "pointer",
                      opacity: deleteMutation.isPending ? 0.6 : 1,
                    }}
                  >
                    {t("edit.delete")}
                  </button>
                )}
              </div>
            )}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                padding: "12px",
              }}
            >
              {records.map((p, idx) => {
                const key = itemKey(p.spuNo, p.channel);
                const isSelected = selected.has(key);
                const bg = TILE_BG[idx % TILE_BG.length];
                return (
                  <div
                    key={key}
                    style={{ position: "relative" }}
                    onClick={
                      editMode
                        ? () => toggleSelect(key)
                        : undefined
                    }
                  >
                    {/* Selection checkbox overlay in edit mode */}
                    {editMode && (
                      <div
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          zIndex: 2,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: isSelected ? "var(--color-cta)" : "rgba(255,255,255,0.9)",
                          border: isSelected ? "none" : "2px solid var(--color-border)",
                          display: "grid",
                          placeItems: "center",
                          fontSize: 11,
                          color: "#fff",
                          pointerEvents: "none",
                        }}
                      >
                        {isSelected && "✓"}
                      </div>
                    )}
                    {editMode ? (
                      /* In edit mode: non-navigable card */
                      <div
                        style={{
                          background: "#fff",
                          border: `1.5px solid ${isSelected ? "var(--color-cta)" : "var(--color-border)"}`,
                          borderRadius: 10,
                          padding: 6,
                          display: "flex",
                          flexDirection: "column",
                          gap: 5,
                          cursor: "pointer",
                          opacity: isSelected ? 0.9 : 1,
                        }}
                      >
                        <FavProductImage imageUrl={p.imageUrl} title={p.title} bg={bg} />
                        <FavProductTitle title={p.title} />
                        <FavProductPrice price={p.priceRubKopecks} />
                      </div>
                    ) : (
                      <Link
                        to={"/product/$channel/$spuNo" as never}
                        params={{ channel: p.channel, spuNo: p.spuNo } as never}
                        style={{
                          background: "#fff",
                          border: "1px solid var(--color-border)",
                          borderRadius: 10,
                          padding: 6,
                          textDecoration: "none",
                          color: "inherit",
                          display: "flex",
                          flexDirection: "column",
                          gap: 5,
                        }}
                      >
                        <FavProductImage imageUrl={p.imageUrl} title={p.title} bg={bg} />
                        <FavProductTitle title={p.title} />
                        <FavProductPrice price={p.priceRubKopecks} />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── Shop list (shop tab) ── */}
        {activeQuery.isSuccess && records.length > 0 && tab === "shop" && (
          <>
            {editMode && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  background: "#fff",
                  borderBottom: "1px solid var(--color-border)",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  onClick={selected.size === records.length ? clearSelection : selectAll}
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--color-text-muted)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {selected.size === records.length ? "☑ " : "☐ "}
                  {t("edit.selected", { n: selected.size })}
                </button>
                {selected.size > 0 && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    style={{
                      padding: "6px 14px",
                      background: "var(--color-danger, #FF3B30)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: deleteMutation.isPending ? "not-allowed" : "pointer",
                      opacity: deleteMutation.isPending ? 0.6 : 1,
                    }}
                  >
                    {t("edit.delete")}
                  </button>
                )}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {records.map((p, idx) => {
                const key = itemKey(p.spuNo, p.channel);
                const isSelected = selected.has(key);
                const bg = TILE_BG[idx % TILE_BG.length];
                return (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 12px",
                      background: isSelected ? "var(--color-surface, #F5F5F7)" : "#fff",
                      borderBottom: "1px solid var(--color-border)",
                      cursor: editMode ? "pointer" : "default",
                    }}
                    onClick={editMode ? () => toggleSelect(key) : undefined}
                  >
                    {editMode && (
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: isSelected ? "var(--color-cta)" : "transparent",
                          border: isSelected ? "none" : "2px solid var(--color-border)",
                          display: "grid",
                          placeItems: "center",
                          fontSize: 11,
                          color: "#fff",
                        }}
                      >
                        {isSelected && "✓"}
                      </div>
                    )}
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: bg,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        overflow: "hidden",
                      }}
                    >
                      {p.imageUrl ? (
                        <img
                          src={p.imageUrl}
                          alt={p.title}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <span style={{ fontSize: 18 }}>🏪</span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.title || p.spuNo}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--color-text-muted)",
                          marginTop: 2,
                        }}
                      >
                        {t("shop_card.shop_id_label")}: {p.spuNo}
                      </div>
                    </div>
                    {!editMode && (
                      <span
                        style={{
                          fontSize: 14,
                          color: "var(--color-text-muted)",
                          flexShrink: 0,
                        }}
                      >
                        →
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </TgPage>
  );
}

// ── Small shared sub-components (same file, page-private) ──

function FavProductImage({
  imageUrl,
  title,
  bg,
}: {
  imageUrl: string;
  title: string;
  bg: string | undefined;
}) {
  return (
    <div
      style={{
        aspectRatio: "1",
        background: bg,
        borderRadius: 8,
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={title}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        // Design: colorful gradient placeholder, not emoji
        <div
          style={{
            width: "100%",
            height: "100%",
            background: bg,
            borderRadius: 8,
          }}
        />
      )}
    </div>
  );
}

function FavProductTitle({ title }: { title: string }) {
  return (
    <div
      style={{
        fontSize: 11,
        lineHeight: 1.3,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      {title}
    </div>
  );
}

function FavProductPrice({ price }: { price: number }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-display, -apple-system, system-ui, sans-serif)",
        fontSize: 13,
        fontWeight: 900,
        color: "var(--color-price)",
      }}
    >
      {price > 0 ? formatPrice(price, "ru-RU", "RUB") : "—"}
    </div>
  );
}
